/**
 * What went out through the mailbox — Aaron: "I want an email log of outgoing
 * emails sent through the microsoft 365 account. I want to be able to click
 * on them and actually see the content that was sent."
 *
 * The second tab of **Audit logs**, and the same list engine as the first.
 * Two things make it different from every other list in the console:
 *
 * **A row opens.** The audit log deliberately has no detail page, because a
 * row *is* the record; here the row is a summary of something longer, and the
 * longer thing is the whole point of the screen.
 *
 * **The body is drawn in a sandboxed `<iframe>`, never into the page.** The
 * stored HTML was composed by this product, but it is still stored markup
 * being rendered on an authenticated screen, and `srcDoc` with an empty
 * `sandbox` is the one way to show it that cannot run a script, cannot reach
 * this document, and cannot navigate the top frame. `dangerouslySetInnerHTML`
 * would do all three.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  ApiError, OPENED_HONESTY, openedTimes, readerShort, stoppedWords, type StoppedReason,
} from '@rowted/shared'
import { adminRequest } from '../../lib/admin-api.js'
import { ListScreen } from '../../components/list-screen.js'
import { useList, type ListRow } from '../../lib/use-list.js'
import { Button, Tag } from '../../components/primitives.js'
import { Modal } from '../../components/overlays.js'
import { IconSpinner, IconWarn } from '../../frame/icons.js'
import { formatListDate } from '../../lib/format.js'

/** One message, as `GET /platform/email-log/:id` answers. */
interface Message {
  id: string
  sent_at: string
  client: string | null
  to_address: string
  /* 0149: who it was copied and blind-copied to, joined like To. */
  cc_address?: string | null
  bcc_address?: string | null
  from_address: string | null
  reply_to: string | null
  subject: string
  body_html: string | null
  body_text: string | null
  redacted: boolean
  status: 'sent' | 'failed'
  failure: string | null
  ms: number | null
  /* 0146: the name it said it was from, the way it went, and the id it carried. */
  from_name: string | null
  sent_as: 'mime' | 'json' | 'json_upload' | 'postmark' | null
  message_id: string | null
  /* 0147: Postmark's own id, and what its events said became of the message. */
  provider_message_id: string | null
  delivered_at: string | null
  bounced_at: string | null
  bounce_type: 'hard' | 'soft' | null
  bounce_reason: string | null
  complained_at: string | null
  /* 0151: whether it asked Postmark to say when it was opened, and what Postmark said. */
  opens_tracked?: boolean
  first_opened_at?: string | null
  last_opened_at?: string | null
  open_count?: number
  open_client?: string | null
  open_device?: string | null
  /* Which of its addresses Postmark has stopped sending to now. */
  stopped?: { address: string; reason: Exclude<StoppedReason, null>; at: string }[]
}

/** How a message went to Microsoft, in a word somebody comparing From lines can use. */
const SENT_AS: Record<NonNullable<Message['sent_as']>, string> = {
  mime: 'Raw MIME message',
  json: 'Graph JSON',
  json_upload: 'Graph JSON, attachments uploaded',
  postmark: 'Postmark',
}

/** What Postmark said became of it, worst first — the same order the list's Delivery column uses. */
function deliveryWords(message: Message): string | null {
  if (message.complained_at) return `Marked as spam · ${when(message.complained_at)}`
  if (message.bounce_type === 'hard' && message.bounced_at) {
    return `Bounced · ${when(message.bounced_at)}${message.bounce_reason ? ` · ${message.bounce_reason}` : ''}`
  }
  if (message.delivered_at) return `Delivered · ${when(message.delivered_at)}`
  if (message.bounce_type === 'soft' && message.bounced_at) {
    return `Soft bounce · ${when(message.bounced_at)}${message.bounce_reason ? ` · ${message.bounce_reason}` : ''}`
  }
  return null
}

const DELIVERY_TAG: Record<string, { tone: 'ok' | 'warn' | 'bad'; label: string }> = {
  delivered: { tone: 'ok', label: 'Delivered' },
  soft_bounced: { tone: 'warn', label: 'Soft bounce' },
  bounced: { tone: 'bad', label: 'Bounced' },
  complained: { tone: 'bad', label: 'Marked as spam' },
}

/**
 * What Postmark's opens said (0151): how many, the latest and where — or that
 * it was watched and nobody has, or that it was never asked to watch.
 */
function openedWords(message: Message): string {
  const count = message.open_count ?? 0
  if (count > 0 && message.last_opened_at) {
    const where = readerShort(message.open_client, message.open_device)
    return `Opened ${when(message.last_opened_at)}${openedTimes(count)}${where ? ` · ${where}` : ''}`
  }
  return message.opens_tracked ? 'Watched — not opened yet' : 'Not watched for opens'
}

/** A day in the reader's zone, for `stoppedWords`. */
const localDay = (iso: string): string => new Date(iso).toLocaleDateString('en-CA')

/** `Thu Sep 17 · 9:52 am` — the audit log's words, so the two tabs read alike. */
function when(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return iso
  const time = at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  return `${formatListDate(at)} · ${time.toLowerCase()}`
}

export function EmailLogTab() {
  const [open, setOpen] = useState<string | null>(null)

  const list = useList('email-log', {
    /* The row menu and the opener need these whatever the columns are — and the Opened cell its count. */
    alwaysFetch: ['id', 'subject', 'openCount', 'opensTracked'],
    request: adminRequest,
  })

  return (
    <>
      <ListScreen
        area="email-log"
        title="Email log"
        chrome="page"
        /* A card counting messages sent all time only ever goes up. */
        cards={false}
        list={list}
        request={adminRequest}
        /*
         * When, like the audit log — nobody looks for one email, they look at
         * a stretch of time — with the subject under it, because that is what
         * tells two messages of the same minute apart.
         */
        primaryColumn={{
          field: 'at',
          header: 'When',
          render: (row: ListRow) => ({
            primary: row.at ? when(String(row.at)) : '',
            secondary: String(row.subject ?? ''),
          }),
        }}
        onRowClick={(row: ListRow) => setOpen(String(row.id))}
        cell={{
          status: (row) => (row.status === 'sent'
            ? <Tag tone="ok">Sent</Tag>
            : <Tag tone="bad">Did not send</Tag>),
          client: (row) => (row.client === 'Rowted'
            ? <span className="text-text-faint">Rowted</span>
            : String(row.client ?? '')),
          redacted: (row) => (row.redacted
            ? <Tag tone="warn">A link</Tag>
            : <span className="text-text-faint">—</span>),
          delivery: (row) => {
            const tag = row.delivery ? DELIVERY_TAG[String(row.delivery)] : undefined
            return tag ? <Tag tone={tag.tone}>{tag.label}</Tag> : <span className="text-text-faint">—</span>
          },
          ms: (row) => (row.ms === null || row.ms === undefined
            ? <span className="text-text-faint">—</span>
            : `${Number(row.ms).toLocaleString()} ms`),
          /* The latest open and how many (0151), with the honesty on hover. */
          opened: (row) => (row.opened
            ? <span title={OPENED_HONESTY}>{when(String(row.opened))}{openedTimes(Number(row.openCount ?? 0))}</span>
            : row.opensTracked
              ? <span className="text-text-faint">Not yet</span>
              : <span className="text-text-faint" title="This email did not ask Postmark to say when it was opened.">—</span>),
          stopped: (row) => (row.stopped
            ? <Tag tone="bad">Stopped</Tag>
            : <span className="text-text-faint">—</span>),
        }}
        emptyTitle="Nothing has been sent yet"
        emptyBody="Every message that leaves the connected mailbox lands here — an invoice, a
                   reminder, an invite — whether or not it got through."
      />

      {open && <MessageDialog id={open} onClose={() => setOpen(null)} />}
    </>
  )
}

/**
 * One message, as it was sent.
 *
 * Asked for when it opens rather than carried on the row: a list of five
 * hundred rows does not want five hundred bodies in it, and the request is
 * also what writes the §C.5 row saying somebody read this one.
 */
function MessageDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const [message, setMessage] = useState<Message | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setMessage(await adminRequest<Message>(`/email-log/${id}`))
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That message could not be read.')
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  return (
    <Modal
      open
      onClose={onClose}
      title={message?.subject ?? 'Loading…'}
      width={760}
      footerNote={message ? `${message.to_address} · ${when(message.sent_at)}` : ''}
      footer={<Button variant="primary" onClick={onClose}>Done</Button>}
    >
      {error && (
        <p role="alert" className="m-0 rounded-card bg-danger-bg px-3 py-2 text-cell text-danger">
          {error}
        </p>
      )}

      {!message && !error && (
        <p className="m-0 flex items-center gap-2 text-cell text-text-muted">
          <IconSpinner size={15} /> Reading it back…
        </p>
      )}

      {message && (
        <div className="flex flex-col gap-3">
          <dl className="m-0 grid grid-cols-[88px_1fr] gap-x-3 gap-y-1 text-cell">
            <Row label="To" value={message.to_address} />
            {message.cc_address && <Row label="Cc" value={message.cc_address} />}
            {message.bcc_address && <Row label="Bcc" value={message.bcc_address} />}
            {message.from_address && (
              <Row label="From"
                value={message.from_name ? `${message.from_name} <${message.from_address}>` : message.from_address} />
            )}
            {message.reply_to && <Row label="Reply to" value={message.reply_to} />}
            <Row label="Client" value={message.client ?? 'Rowted’s own'} />
            {message.sent_as && <Row label="Sent as" value={SENT_AS[message.sent_as]} />}
            {message.message_id && <Row label="Message-ID" value={`<${message.message_id}>`} />}
            {message.provider_message_id && <Row label="Postmark id" value={message.provider_message_id} />}
            {deliveryWords(message) && <Row label="Delivery" value={deliveryWords(message)!} />}
            {message.sent_as === 'postmark' && <Row label="Opens" value={openedWords(message)} />}
            {(message.open_count ?? 0) > 1 && message.first_opened_at && (
              <Row label="First opened" value={when(message.first_opened_at)} />
            )}
            <Row
              label="Outcome"
              value={message.status === 'sent'
                ? `Sent${message.ms === null ? '' : ` in ${message.ms} ms`}`
                : message.failure ?? 'It did not send.'}
            />
          </dl>

          {/*
            * Said where it happened. A link that has been cut is otherwise
            * indistinguishable from a link that was never there, and somebody
            * reading this needs to know which — a set-password email with no
            * link in it would look like a bug in the email rather than the
            * log doing its job.
            */}
          {(message.open_count ?? 0) > 0 && (
            <p className="m-0 text-[11.5px] leading-[1.45] text-text-muted">{OPENED_HONESTY}</p>
          )}

          {(message.stopped ?? []).map((one) => (
            <p key={one.address} role="status"
              className="m-0 flex items-start gap-2 rounded-field bg-danger-bg px-3 py-2.5 text-[11.5px]
                         leading-[1.45] text-danger">
              <span className="mt-px shrink-0"><IconWarn size={14} /></span>
              <span>{stoppedWords({ address: one.address, reason: one.reason, day: localDay(one.at) },
                { isTo: false, advice: false })}</span>
            </p>
          ))}

          {message.redacted && (
            <p className="m-0 flex items-start gap-2 rounded-field bg-warn-bg px-3 py-2.5
                          text-[11.5px] leading-[1.45] text-warn">
              <span className="mt-px shrink-0"><IconWarn size={14} /></span>
              <span>
                This message carried a sign-in link. The link is shown but the part that
                makes it work was never stored — a log of live credentials is a credential
                store. Use <b className="font-semibold">Resend invite</b> to issue a new one.
              </span>
            </p>
          )}

          <Body message={message} />
        </div>
      )}
    </Modal>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="m-0 text-text-muted">{label}</dt>
      <dd className="m-0 min-w-0 break-words text-text">{value}</dd>
    </>
  )
}

/**
 * The message itself.
 *
 * HTML goes into an `<iframe srcDoc sandbox="">` — no scripts, no access to
 * this document, no navigating the top frame — and plain text into a `<pre>`,
 * because a plain-text email's line breaks *are* the formatting.
 */
function Body({ message }: { message: Message }) {
  if (message.body_html) {
    return (
      <iframe
        title="What the message said"
        sandbox=""
        srcDoc={message.body_html}
        className="h-[360px] w-full rounded-field border border-line bg-surface"
      />
    )
  }
  if (message.body_text) {
    return (
      <pre className="m-0 max-h-[360px] overflow-auto whitespace-pre-wrap break-words
                      rounded-field border border-line bg-surface-alt p-3 text-cell text-text">
        {message.body_text}
      </pre>
    )
  }
  return (
    <p className="m-0 rounded-field border border-line bg-surface-alt p-3 text-cell
                  text-text-muted">
      This message had no body stored.
    </p>
  )
}
